import { useMarketAccountsContext, useMarketsContext, useSecuritiesContext } from '@talos/kyoko';
import { useCallback } from 'react';
import { useDefaultAggregation } from '../../../../../providers/DefaultAggregationContext';
import { useTradingSettings } from '../../../../../providers/TradingSettingsContext';
import { useMarketDataMarketSelectorItemBuilder } from '../../../../MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import { useMarketSelectorAggregationPresets } from '../../../../MarketSelector/presets/useMarketSelectorAggregationPresets';
import {
  buildTradingSymbolDefaultPanelMarkets,
  type TradingSymbolPanelMarkets,
} from '../../../../MarketSelector/tradingSymbolDefaultMarkets';

/**
 * Builds the Deep Dive panel fields to apply when the user changes symbols.
 * Deep Dive uses default aggregations but keeps unified-liquidity markets out of the selector options.
 */
export function useBuildDeepDiveSymbolChangePanelParams(): (nextSymbol: string) => TradingSymbolPanelMarkets {
  const { useNewMarketSelector } = useTradingSettings();
  const { defaultAggregationId } = useDefaultAggregation();
  const { aggregationPresets } = useMarketSelectorAggregationPresets();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const buildMarketDataMarketSelectorItems = useMarketDataMarketSelectorItemBuilder();

  return useCallback(
    (nextSymbol: string) => {
      const security = securitiesBySymbol.get(nextSymbol);
      const marketSelectorItems = buildMarketDataMarketSelectorItems({
        symbol: nextSymbol,
        includeUnifiedLiquidityMarkets: false,
      });

      return buildTradingSymbolDefaultPanelMarkets({
        nextSymbol,
        security,
        useNewMarketSelector,
        defaultAggregationId,
        aggregationPresets,
        marketsByName,
        marketAccountsByName,
        marketSelectorItems,
      });
    },
    [
      securitiesBySymbol,
      useNewMarketSelector,
      defaultAggregationId,
      aggregationPresets,
      marketsByName,
      marketAccountsByName,
      buildMarketDataMarketSelectorItems,
    ]
  );
}
